<template>

  <div>
    <Toolbar>
      <template #actions>
        <div class="d-flex align-items-center gap-3">
        <template v-if="isExpandPatientData && schedule">
        <div class="d-flex align-items-center gap-3">
          <div class="d-flex align-items-center bg-light-primary rounded px-3 py-2">
            <i class="bi bi-alarm text-primary me-2"></i>
            <span class="text-primary fw-bold fs-6">{{ activeTimerString }}</span>
          </div>
          <div class="d-flex align-items-center bg-light-secondary rounded px-3 py-2">
            <i class="bi bi-person me-2"></i>
            <span class="fw-bold fs-6">{{ schedule?.patient?.patient_name }}</span>
          </div>
        </div>
        </template>
        <button type="button" class="btn btn-icon btn-light" title="Configurações do atendimento"
                aria-label="Configurações do atendimento" aria-haspopup="dialog" @click="settingsDialogOpen = true">
          <i class="bi bi-gear" aria-hidden="true"></i>
        </button>
        </div>
      </template>
    </Toolbar>

    <AttendanceScheduleDialog v-if="schedule && place" v-model:visible="scheduleDialogOpen"
                              :schedule="schedule" :place="place" />

    <AttendanceSettingsDialog v-model:visible="settingsDialogOpen" :model-value="patientDataSide"
                              :loading="settingsLoading" :saving="settingsSaving" :error="settingsError"
                              @retry="loadAttendanceSettings" @update:modelValue="setPatientDataSide" />

    <AttendanceConsultationDrawer :open="consultationDrawerOpen" :date="selectedHistoryDate"
                                  :patient-name="schedule?.patient?.patient_name"
                                  @close="closeConsultationDrawer" />

    <div class="container-xxl" v-if="histories.length > 0">
      <AttendanceHistoryTimeline :histories="histories"
                                 :selected-date="selectedHistoryDate"
                                 :current-date="schedule?.sched_date"
                                 :current-active="activeWorkspaceTab === 'attendance'"
                                 @current="selectWorkspaceTab('attendance')"
                                 @all="openAllHistory"
                                 @select="handleTimelineDateClick" />
    </div>

    <div class="container-xxl">

      <div class="attendance-layout d-flex flex-column flex-xl-row gap-xl-5"
           :class="{ 'attendance-layout--patient-right': patientDataSide === 'right' }">

        <div class="attendance-patient-toggle flex-column" v-show="isExpandPatientData">
          <button type="button" class="btn btn-white btn-active-light-primary btn-sm mr-10"
                  @click="toggleExpandPatientData">
            <i class="bi" :class="patientDataSide === 'right' ? 'bi-arrow-left-circle' : 'bi-arrow-right-circle'"></i>
          </button>
        </div>
        <div class="attendance-patient-sidebar flex-column flex-lg-row-auto w-100 mb-10"
             :class="this.isScheduleOnline ? 'w-xl-400px' : 'w-xl-300px' " v-show="!isExpandPatientData">

          <PatientCard :schedule="schedule" :patient="schedule?.patient" :side="patientDataSide"
                       @onToggleExpandPatientData="toggleExpandPatientData"
                       @onTimerUpdate="updateTimer"
                       @onEditPatient="openModalEditPatient">
            <template #image v-if="this.isScheduleOnline">
              <JitsiMeeting v-if="this.waitingRoom"
                            :roomName="this.waitingRoom.wr_code"
                            :domain="this.waitingRoom.wr_domain"
                            :jwt="this.waitingRoom.wr_jwt"
                            :configOverwrite="{
                                    subject: 'Teleconsulta',
                                    hideConferenceSubject: false,
                                    disableProfile: true,
                                    startWithVideoMuted: false,
                                    hideConferenceTimer: true,
                                    defaultLocalDisplayName: 'Você',
                                    prejoinPageEnabled: false,
                                    toolbarButtons: [
                                        'microphone', 'camera', 'desktop', 'fullscreen',
                                        'hangup', 'toggle-camera',
                                        'etherpad', 'raisehand',
                                        'select-background', 'tileview', 'chat',
                                    ],
                                }"
                            :interfaceConfigOverwrite="{
                                    APP_NAME: 'Ubbimed',
                                    MOBILE_APP_PROMO: false,
                                    SHOW_JITSI_WATERMARK: false,
                                    DEFAULT_BACKGROUND: '#780f5a',
                                    HIDE_INVITE_MORE_HEADER: true,
                                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                                    SETTINGS_SECTIONS: ['devices'],
                                    LANG_DETECTION: false,
                                }"
                            lang="ptBR"
                            width="100%"
                            height="400px"
                            :userInfo="{
                                    displayName: this.userAuth.user_name,
                                    email: this.userAuth.user_email,
                                }"
                            @onApiReady="handleApiReady"
                            @onReadyToClose="handleReadyToClose"
                            @getIframeRefOnApiReady="handleJitsiIFrameRef1"
              />
            </template>
          </PatientCard>
        </div>

        <div class="attendance-workspace flex-lg-row-fluid">
          <AiBriefingPanel
            v-if="schedule"
            :schedule="schedule"
            :record-types="recordTypes"
            :professional-id="professionalId"
            @open-patient-chat="openAiAssistant({ section: 'patient-chat' })"
          />

          <div class="attendance-workspace-toolbar mb-8">
            <ul class="attendance-workspace-tabs" aria-label="Navegação da consulta" role="tablist">
              <li class="nav-item" role="presentation">
                <button type="button" class="attendance-workspace-tab"
                        :class="{active: activeWorkspaceTab === 'attendance'}"
                        role="tab" :aria-selected="activeWorkspaceTab === 'attendance'"
                        @click="selectWorkspaceTab('attendance')">
                  <i class="bi bi-clipboard2-pulse"></i>
                  <span>Consulta atual</span>
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button type="button" class="attendance-workspace-tab"
                        :class="{active: activeWorkspaceTab === 'history'}"
                        role="tab" :aria-selected="activeWorkspaceTab === 'history'"
                        @click="selectWorkspaceTab('history')">
                  <i class="bi bi-clock-history"></i>
                  <span>Histórico clínico</span>
                </button>
              </li>
            </ul>
            <div class="attendance-workspace-actions">
              <AiAssistantPanel
                ref="aiAssistant"
                :schedule="schedule"
                :medical-records="medicalRecords"
                :record-types="recordTypes"
                :professional-id="professionalId"
                :live-transcript="liveTranscript"
                @processed-transcript="fillMedicalRecordAttributes($event)"
              />
              <div v-if="availableRecordTypes.length" ref="newRecordDropdown" class="new-record-dropdown">
                <button type="button" class="btn btn-secondary ps-7" :aria-expanded="newRecordMenuOpen"
                        aria-haspopup="menu" @click="newRecordMenuOpen = !newRecordMenuOpen"
                        @keydown.esc="newRecordMenuOpen = false">
                  Novo
                  <i class="bi bi-chevron-down ms-2"></i>
                </button>
                <div v-show="newRecordMenuOpen" class="new-record-menu" role="menu">
                  <p class="new-record-menu-title">Tipo de registro</p>
                  <button v-for="recordType in availableRecordTypes" :key="recordType.id_rectype"
                          type="button" role="menuitem" @click="storeMedicalRecord(recordType)">
                    {{ recordType.rectype_name }}
                  </button>
                </div>
              </div>
              <button v-if="canSchedule && schedule?.patient" type="button" class="btn btn-light-primary"
                      aria-haspopup="dialog" @click="scheduleDialogOpen = true">
                <i class="bi bi-calendar-plus" aria-hidden="true"></i> Agendar
              </button>
              <button type="button" class="btn btn-light-danger ps-7 ms-3 border-danger" @click="finishAttendance">
                Encerrar
                <span class="svg-icon svg-icon-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="4" y="11" width="12" height="2" rx="1" fill="currentColor"/>
                      <path
                          d="M5.86875 11.6927L7.62435 10.2297C8.09457 9.83785 8.12683 9.12683 7.69401 8.69401C7.3043 8.3043 6.67836 8.28591 6.26643 8.65206L3.34084 11.2526C2.89332 11.6504 2.89332 12.3496 3.34084 12.7474L6.26643 15.3479C6.67836 15.7141 7.3043 15.6957 7.69401 15.306C8.12683 14.8732 8.09458 14.1621 7.62435 13.7703L5.86875 12.3073C5.67684 12.1474 5.67684 11.8526 5.86875 11.6927Z"
                          fill="currentColor"/>
                      <path
                          d="M8 5V6C8 6.55228 8.44772 7 9 7C9.55228 7 10 6.55228 10 6C10 5.44772 10.4477 5 11 5H18C18.5523 5 19 5.44772 19 6V18C19 18.5523 18.5523 19 18 19H11C10.4477 19 10 18.5523 10 18C10 17.4477 9.55228 17 9 17C8.44772 17 8 17.4477 8 18V19C8 20.1046 8.89543 21 10 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H10C8.89543 3 8 3.89543 8 5Z"
                          fill="#C4C4C4"/>
                    </svg>
                  </span>
              </button>
            </div>
          </div>

          <div class="tab-content w-100">
            <div v-show="activeWorkspaceTab === 'attendance'" class="tab-pane show active" id="tab-attendance" role="tabpanel">
              <AttendanceTab :medical-records="medicalRecords"
                             :schedule="schedule"
                             :record-types="recordTypes"
                             :exam-protocols="examProtocols"
                             :prescription-protocols="prescriptionProtocols"
                             :presc-types="prescTypes"
                             :drugs-admins="drugAdmins"
                             :documentTemplates="documentTemplates"
                             :healthIndicatorGroups="healhImdicatorGroups"
                             :ai-fill-nonce="aiFillNonce"
                             :key="'attendance-' + schedule?.id_sched"
                             :certifiers="certifiers"
                             @edit-patient-contact="openModalEditPatient"
                             @open-ai-assistant="openAiAssistant"
                             @record-removed="getMedicalRecords"/>
            </div>
            <div v-if="historyTabOpened || consultationDrawerOpen" v-show="activeWorkspaceTab === 'history'" class="tab-pane show active" id="tab-history" role="tabpanel">
              <Teleport to="#attendance-consultation-body" :disabled="!consultationDrawerOpen">
              <History :record-types="recordTypes"
                       :schedule="schedule"
                       :exam-protocols="examProtocols"
                       :prescription-protocols="prescriptionProtocols"
                       :presc-types="prescTypes"
                       :drugs-admins="drugAdmins"
                       :documentTemplates="documentTemplates"
                       :healthIndicatorGroups="healhImdicatorGroups"
                       :key="'history-' + schedule?.id_sched"
                       :certifiers="certifiers"
                       :selected-date="selectedHistoryDate"
                       :consultation-only="consultationDrawerOpen"/>
              </Teleport>
            </div>
          </div>
        </div>

      </div>

    </div>

    <ModalPatient
      v-if="currentPatientData"
      :patient="currentPatientData"
      :open-modal="isEditPatientModalOpen"
      :key="'modal-patient-' + currentPatientData?.id_patient"
      @onCloseModal="onCloseModalEditPatient"
      @onPatientUpdated="onPatientUpdated"
    />

  </div>
</template>

<script>
import AttendanceTab from './tab/Attendance';
import PatientCard from "./shared/PatientCard";
import History from './tab/History';
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import {JitsiMeeting} from "@jitsi/vue-sdk";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import AiAssistantPanel from "@/views/attendance/shared/AiAssistantPanel.vue";
import AiBriefingPanel from "@/views/attendance/shared/AiBriefingPanel.vue";
import ModalPatient from "@/views/patient/ModalPatient.vue";
import AttendanceHistoryTimeline from './shared/AttendanceHistoryTimeline.vue';
import AttendanceConsultationDrawer from './shared/AttendanceConsultationDrawer.vue';
import AttendanceSettingsDialog from './shared/AttendanceSettingsDialog.vue';
import AttendanceScheduleDialog from './shared/AttendanceScheduleDialog.vue';
import permission from '../../helpers/permission';
import moment from 'moment';
import { emptyLiveTranscript, fetchLiveTranscript, subscribeLiveTranscript } from '@/helpers/liveTranscript';
import { visibleAttributes } from '@/views/attendance/shared/record-attributes';

export default {
  name: "Attendance",
  components: {
    AiAssistantPanel,
    AiBriefingPanel,
    AttendanceTab,
    History,
    PatientCard,
    ModalPatient,
    JitsiMeeting,
    Toolbar,
    AttendanceHistoryTimeline,
    AttendanceConsultationDrawer,
    AttendanceSettingsDialog,
    AttendanceScheduleDialog,
  },
  data() {
    return {
      date: moment,
      histories: [],
      schedule: null,
      medicalRecords: [],
      recordTypes: [],
      examProtocols: [],
      prescriptionProtocols: [],
      prescTypes: [],
      drugAdmins: [],
      documentTemplates: [],
      healhImdicatorGroups: [],
      certifiers: [],
      isExpandPatientData: true,
      patientDataSide: 'left',
      settingsDialogOpen: false,
      scheduleDialogOpen: false,
      settingsLoading: true,
      settingsSaving: false,
      settingsError: '',
      scheduleId: null,
      professionalId: null,
      waitingRoom: null,
      isScheduleOnline: false,
      height: null,
      jistiReference: null,
      selectedHistoryDate: null,
      consultationDrawerOpen: false,
      activeWorkspaceTab: 'attendance',
      historyTabOpened: false,
      // Chave para localStorage
      PATIENT_CARD_STATE_KEY: 'ubbi_patient_card_expanded_state',
      // Timer para exibir no toolbar quando PatientCard estiver minimizado
      activeTimerString: '00:00:00',
      currentPatientData: null,
      isEditPatientModalOpen: false,
      liveTranscript: emptyLiveTranscript(),
      leaveLiveTranscript: null,
      liveTranscriptRetry: null,
      liveTranscriptEchoTries: 0,
      aiFillNonce: 0,
      newRecordMenuOpen: false,
    }
  },
  async created() {
    // Carregar preferência do localStorage
    this.loadPatientCardState();

    this.professionalId = this.userAuth.id_prof;
    this.loadAttendanceSettings();
    this.scheduleId = this.$route.params.scheduleId;
    this.findSchedule().then(() => {
      this.isScheduleOnline = this.schedule?.sched_online;
      if (this.isScheduleOnline) {
        this.getWaitingRoom();
      }

      this.loadInitialMedicalRecords();
      this.getRecordTypes();
      this.getExamProtocols();
      this.getPrescriptionProtocols();
      this.getPrescTypes();
      this.getDrugAdmin();
      this.getDocumentTemplates();
      this.getHealthIndicatorGroups();
      this.getCertifiers();
      this.getHistories();
      this.bindLiveTranscript();
    });
  },
  mounted() {
    KTDrawer.createInstances();
    this.leaveRoom = this.leaveRoom.bind(this);
    window.addEventListener('pagehide', this.leaveRoom);
    document.addEventListener('click', this.closeNewRecordMenuOnOutsideClick);
  },
  beforeUnmount() {
    window.removeEventListener('pagehide', this.leaveRoom);
    document.removeEventListener('click', this.closeNewRecordMenuOnOutsideClick);
    this.leaveRoom();
    this.unbindLiveTranscript();
  },
  computed: {
    canSchedule() { return permission.has('Store Schedule'); },
    place() {
      return this.$store.state.auth.place;
    },
    userAuth() {
      return this.$store.state.auth.user;
    },
    availableRecordTypes() {
      return this.recordTypes.filter(recordType => !this.existsMedicalRecord(recordType));
    },
  },
  methods: {
    closeNewRecordMenuOnOutsideClick(event) {
      if (!this.$refs.newRecordDropdown?.contains(event.target)) {
        this.newRecordMenuOpen = false;
      }
    },
    bindLiveTranscript() {
      this.unbindLiveTranscript();

      if (!this.professionalId || !this.scheduleId) {
        return;
      }

      fetchLiveTranscript(this.professionalId, this.scheduleId).then((snapshot) => {
        this.liveTranscript = snapshot;
        if (snapshot.listening) {
          this.$refs.aiAssistant?.openSection('transcription');
        }
      });

      if (!window.Echo && this.liveTranscriptEchoTries < 5) {
        this.liveTranscriptEchoTries += 1;
        this.liveTranscriptRetry = setTimeout(() => this.bindLiveTranscript(), 1500);
        return;
      }

      this.leaveLiveTranscript = subscribeLiveTranscript(this.scheduleId, (event) => {
        this.liveTranscript = {
          ...this.liveTranscript,
          text: event.text ?? '',
          seq: event.seq ?? 0,
          listening: !!event.listening,
          faithful_transcript: event.faithful_transcript ?? this.liveTranscript.faithful_transcript,
        };

        if (event.listening) {
          this.$refs.aiAssistant?.openSection('transcription');
        }
      });
    },
    unbindLiveTranscript() {
      if (this.liveTranscriptRetry) {
        clearTimeout(this.liveTranscriptRetry);
        this.liveTranscriptRetry = null;
      }
      if (this.leaveLiveTranscript) {
        this.leaveLiveTranscript();
        this.leaveLiveTranscript = null;
      }
    },
    fillMedicalRecordAttributes(event) {
      const attributes = event?.attributes && typeof event.attributes === 'object'
        ? event.attributes
        : event;

      this.medicalRecords.forEach((mr) => {
        visibleAttributes(mr.attributes).forEach(attribute => {
          if (attribute.filled_by_ai === false) {
            return;
          }

          const value = attributes[attribute.id_medrec_attr];
          if (!value) {
            return;
          }

          if (attribute.text) {
            attribute.text = attribute.text + ' - ' + value;
          } else {
            attribute.text = value;
          }
        });

        if (event?.cids?.length && (mr.record_type?.allow_cid || mr.record_type?.allow_document)) {
          const current = mr.cids || [];
          const known = new Set(current.map((cid) => cid.id_cid));
          mr.cids = current.concat(event.cids.filter((cid) => !known.has(cid.id_cid)));
        }
      });

      if (event?.cids?.length || event?.exams?.length || event?.health_data?.length) {
        this.aiFillNonce += 1;
      }
    },
    openAiAssistant({ section = null, medrecId = null } = {}) {
      this.$refs.aiAssistant?.open(section, { medrecId });
    },
    selectWorkspaceTab(tab) {
      this.activeWorkspaceTab = tab;
      if (tab === 'history') {
        this.historyTabOpened = true;
      }
      if (tab === 'attendance') {
        this.selectedHistoryDate = null;
      }
    },
    getWaitingRoom() {
      // A sala é por agendamento: sem schedule_id a API responde 400 e o
      // profissional fica sem sala, enquanto o paciente entra na dele.
      return http.get(
          `/places/${this.place.id_place}/professionals/${this.professionalId}/waiting-rooms/opened`,
          { params: { schedule_id: this.scheduleId } },
      ).then((response) => {
        this.waitingRoom = response.data;

        return response;
      });
    },
    finishCall() {
      http.patch(`/places/${this.place.id_place}/professionals/${this.professionalId}/attendance/schedules/${this.scheduleId}/finish-call`).then((response) => {
        Alert.success(
            'O paciente saiu da chamada e não pode mais entrar na sala de atendimento.',
            'Chamada finalizada'
        );
      });
    },
    finishAttendance() {
      Swal.fire({
        title: 'Tem certeza que deseja finalizar o atendimento?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, Finalizar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: 'btn btn-secundary'
        }
      }).then(async (result) => {
        if (result.isConfirmed) {

          if (this.schedule.sched_online) {
            const {value: schedSuccessfull} = await Swal.fire({
              width: 600,
              allowOutsideClick: false,
              title: 'A consulta foi realizada com sucesso?',
              input: 'radio',
              inputOptions: {
                'yes': "Sim, Consegui atender o paciente. <br>",
                'no': "Não, Permitir que o paciente reagende a consulta.<br>",
              },
              inputValidator: (value) => {
                if (!value) {
                  return 'Informe se a consulta foi realizada com sucesso.'
                }
              },
              icon: 'warning',
              confirmButtonText: 'Finalizar Atendimento',
              customClass: {
                confirmButton: "btn btn-success",
              }
            });

            let isSchedSuccessfull = schedSuccessfull === 'yes';

            http.patch(`/places/${this.place.id_place}/professionals/${this.professionalId}/attendance/schedules/${this.scheduleId}/finish`, {sched_successfull: isSchedSuccessfull}).then((response) => {
              this.schedule = response.data;
              this.$router.push('/attendances');
            });
          } else {
            http.patch(`/places/${this.place.id_place}/professionals/${this.professionalId}/attendance/schedules/${this.scheduleId}/finish`).then((response) => {
              this.schedule = response.data;
              this.$router.push('/attendances');
            });
          }

        }
      });
    },
    toggleExpandPatientData() {
      this.isExpandPatientData = !this.isExpandPatientData;
      // Salvar preferência no localStorage
      this.savePatientCardState();
    },
    openModalEditPatient() {
      if (!this.schedule?.id_patient || !this.place?.id_place) {
        return;
      }

      http.get(`/places/${this.place.id_place}/patients/${this.schedule.id_patient}`)
        .then((response) => {
          this.currentPatientData = response.data;
          this.isEditPatientModalOpen = true;
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? 'Erro ao carregar paciente.');
        });
    },
    onCloseModalEditPatient() {
      this.isEditPatientModalOpen = false;
      this.currentPatientData = null;
    },
    onPatientUpdated() {
      this.findSchedule();
    },
    findSchedule() {
      return http.get(`/places/${this.place.id_place}/professionals/${this.professionalId}/attendance/schedules/${this.scheduleId}`).then((response) => {
        this.schedule = response.data;

        return response;
      }).catch((e) => {
        let title = e.response.status === 404 ? 'Agendamento não encontrado.' : 'Falha ao carregar o agendamento.';

        Swal.fire({
          title: title,
          text: 'O agendamento não existe ou não pode mais ser atendido.',
          icon: 'error',
          showCancelButton: false,
          confirmButtonText: 'OK',
          customClass: {
            confirmButton: "btn btn-danger",
          }
        }).then(() => {
          this.$router.push('/attendances');
        });

      });
    },
    getRecordTypes() {
      if (!this.professionalId)
        return;

      http.get(`/professionals/${this.professionalId}/record-types/actives`).then((response) => {
        this.recordTypes = response.data;
      });
    },
    async loadInitialMedicalRecords() {
      await this.getMedicalRecords();
      await this.$nextTick();
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    },
    getMedicalRecords() {
      if (!this.scheduleId)
        return;

      if (!this.schedule?.id_prof)
        return;

      return http.get(`/places/${this.place.id_place}/schedules/${this.scheduleId}/medical-records`).then((response) => {
        this.medicalRecords = response.data;

        return response;
      });
    },
    getHistories() {
      if (!this.scheduleId)
        return;

      if (!this.schedule?.id_patient)
        return;

      if (!this.schedule?.id_prof)
        return;

      return http.get(`/professionals/${this.schedule?.id_prof}/patients/${this.schedule?.id_patient}/medical-records/history-dates`, {
        params: { current_schedule_id: this.scheduleId }
      }).then((response) => {

        this.histories = Array.from(
            new Set(response.data.map(item => item.medrec_date))
        ).map(date => ({ date }));

        return response;
      });
    },
    storeMedicalRecord(recordType) {
      this.newRecordMenuOpen = false;

      let payload = {
        "id_rectype": recordType.id_rectype,
        "id_prof": recordType.id_prof,
        "id_patient": this.schedule.id_patient,
        "id_sched": this.scheduleId,
        "id_place": this.place.id_place,
      };

      http.post(`/professionals/${this.professionalId}/patients/${this.schedule.id_patient}/medical-records`, payload).then((response) => {
        this.getMedicalRecords();
      });
    },
    getPrescriptionProtocols() {
      if (!this.schedule?.id_prof)
        return;

      http.get(`/professionals/${this.schedule?.id_prof}/prescription-models/actives`).then((response) => {
        this.prescriptionProtocols = response.data;
      });
    },
    getPrescTypes() {
      this.precrTypes = [];
      http.get(`/prescription-types`).then((response) => {
        this.prescTypes = response.data;
      });
    },
    getDrugAdmin() {
      http.get(`drug-administrations`).then((response) => {
        this.drugAdmins = response.data;
      });
    },
    getExamProtocols() {
      if (!this.schedule?.id_prof)
        return;

      http.get(`/professionals/${this.schedule?.id_prof}/exam-protocols`).then((response) => {
        this.examProtocols = response.data;
      });
    },
    getDocumentTemplates() {
      if (!this.schedule?.id_prof)
        return;

      http.get(`/places/${this.place.id_place}/professionals/${this.schedule?.id_prof}/document-templates`).then((response) => {
        this.documentTemplates = response.data;
      });
    },
    getHealthIndicatorGroups() {
      if (!this.schedule?.id_prof)
        return;

      http.get(`/professionals/${this.schedule?.id_prof}/health-indicator-groups`).then((response) => {
        this.healhImdicatorGroups = response.data;
      });
    },
    existsMedicalRecord(recordType) {
      return this.medicalRecords.findIndex(({id_rectype}) => id_rectype === recordType.id_rectype) >= 0;
    },
    getCertifiers() {
      http.get(`/digital-certificates/certifiers`).then((response) => {
        this.certifiers = response.data;
      });
    },
    // Sair da tela sem encerrar a sessão deixa o profissional na sala: ao voltar
    // ele aparece duas vezes até o servidor derrubar o fantasma.
    leaveRoom() {
      const reference = this.jistiReference;
      if (!reference) return;

      this.jistiReference = null;

      try {
        reference.executeCommand('hangup');
      } catch (e) {
        // a chamada já tinha sido encerrada
      }

      try {
        reference.dispose();
      } catch (e) {
        // nada a liberar
      }
    },
    handleApiReady(jistiReference) {
      this.jistiReference = jistiReference;

      this.jistiReference.addListener('incomingMessage', (data) => {
        let url = `/places/${this.place.id_place}/professionals/${this.schedule?.id_prof}/attendance/schedules/${this.scheduleId}/chat-messages`;
        let payload = {
          name: data.nick,
          message: data.message,
          id_user: this.schedule.patient.id_user,
        };

        http.post(url, payload);
      });
    },
    handleReadyToClose() {
      this.finishCall();

      this.jistiReference?.getParticipantsInfo()?.forEach((participant) => {
        this.jistiReference.executeCommand('kickParticipant', participant.id);
      });
    },
    handleJitsiIFrameRef1(iframeRef) {
      iframeRef.style.width = '100%';
      iframeRef.style.height = '400px';
      // customizar o iframe do jitsi
    },
    handleTimelineDateClick(date) {
      this.selectedHistoryDate = date;
      this.historyTabOpened = true;
      this.consultationDrawerOpen = true;
    },
    closeConsultationDrawer() {
      this.consultationDrawerOpen = false;
      this.selectedHistoryDate = null;
    },
    openAllHistory() {
      this.historyTabOpened = true;
      this.closeConsultationDrawer();
      this.activeWorkspaceTab = 'history';
    },
    // Métodos para gerenciar localStorage
    async loadAttendanceSettings() {
      this.settingsLoading = true;
      this.settingsError = '';
      try {
        const response = await http.get(`/professionals/${this.professionalId}/attendance-settings`);
        this.patientDataSide = response.data.patient_data_side === 'right' ? 'right' : 'left';
      } catch {
        this.settingsError = 'Não foi possível carregar a configuração do profissional.';
      } finally {
        this.settingsLoading = false;
      }
    },
    async setPatientDataSide(side) {
      if (this.settingsSaving || this.settingsLoading || side === this.patientDataSide) return;
      const previousSide = this.patientDataSide;
      this.patientDataSide = side === 'right' ? 'right' : 'left';
      this.settingsSaving = true;
      this.settingsError = '';
      try {
        await http.put(`/professionals/${this.professionalId}/attendance-settings`, { patient_data_side: this.patientDataSide });
      } catch {
        this.patientDataSide = previousSide;
        this.settingsError = 'Não foi possível salvar. A posição anterior foi mantida. Tente selecionar novamente.';
      } finally {
        this.settingsSaving = false;
      }
    },
    loadPatientCardState() {
      try {
        const savedState = localStorage.getItem(this.PATIENT_CARD_STATE_KEY);
        if (savedState !== null) {
          this.isExpandPatientData = JSON.parse(savedState);
        }
      } catch (error) {
        console.warn('Erro ao carregar estado do PatientCard do localStorage:', error);
        // Manter valor padrão (true = fechado)
        this.isExpandPatientData = true;
      }
    },
    savePatientCardState() {
      try {
        localStorage.setItem(this.PATIENT_CARD_STATE_KEY, JSON.stringify(this.isExpandPatientData));
      } catch (error) {
        console.warn('Erro ao salvar estado do PatientCard no localStorage:', error);
      }
    },
    updateTimer(timerString) {
      this.activeTimerString = timerString;
    },
  }
}
</script>

<style>
@media (min-width: 1200px) {
  .attendance-layout--patient-right > .attendance-patient-sidebar,
  .attendance-layout--patient-right > .attendance-patient-toggle { order: 2; }
  .attendance-workspace { min-width: 0; }

  .attendance-patient-sidebar {
    --patient-sidebar-top: 16px;
    position: sticky;
    top: var(--patient-sidebar-top);
    align-self: flex-start;
    max-height: calc(100dvh - var(--patient-sidebar-top) - 16px);
    overflow-y: auto;
    scrollbar-width: thin;
  }
  .header-fixed .attendance-patient-sidebar { --patient-sidebar-top: 81px; }
  .header-fixed.toolbar-fixed .attendance-patient-sidebar {
    --patient-sidebar-top: calc(81px + var(--kt-toolbar-height, 75px));
  }
}

.attendance-workspace-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(20, 55, 94, 0.05);
}

.attendance-workspace-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  margin: 0;
  list-style: none;
  border-radius: 9px;
  background: #f4f6f8;
}

.attendance-workspace-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 9px 16px;
  border: 1px solid transparent;
  border-radius: 7px;
  color: #667085;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  background: transparent;
  transition: color 150ms ease, background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
}

.attendance-workspace-tab i {
  font-size: 16px;
}

.attendance-workspace-tab:hover {
  color: #780f5a;
  background: rgba(120, 15, 90, 0.06);
}

.attendance-workspace-tab.active {
  color: #780f5a;
  border-color: rgba(120, 15, 90, 0.16);
  background: #fff;
  box-shadow: 0 2px 6px rgba(120, 15, 90, 0.1);
}

.attendance-workspace-tab:focus-visible {
  outline: 3px solid rgba(120, 15, 90, 0.2);
  outline-offset: 2px;
}

.attendance-workspace-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.new-record-dropdown {
  position: relative;
}

.new-record-menu {
  position: absolute;
  z-index: 1050;
  top: calc(100% + 8px);
  right: 0;
  width: 250px;
  padding: 8px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.14);
}

.new-record-menu-title {
  padding: 6px 10px;
  margin: 0 0 4px;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.new-record-menu button {
  display: block;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 7px;
  color: #344054;
  text-align: left;
  background: transparent;
  font-weight: 600;
}

.new-record-menu button:hover,
.new-record-menu button:focus-visible {
  color: #780f5a;
  background: rgba(120, 15, 90, 0.07);
  outline: none;
}

@media (max-width: 991.98px) {
  .attendance-workspace-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .attendance-workspace-tabs,
  .attendance-workspace-actions {
    width: 100%;
  }

  .attendance-workspace-tab {
    flex: 1;
  }

  .attendance-workspace-actions {
    justify-content: flex-end;
    padding: 0 4px 4px;
  }
}

@media (max-width: 575.98px) {
  .attendance-workspace-tab {
    padding-inline: 10px;
  }

  .attendance-workspace-actions {
    flex-wrap: wrap;
  }

}

.swal2-radio {
  display: block !important;
}
</style>
